import { Group, Notification, NotificationProps, Box } from '@mantine/core';
import { NotificationData, notifications } from '@mantine/notifications';
import styles from './Alert.module.css';
import { cva, VariantProps } from 'class-variance-authority';
import {
    IconInfoCircle,
    IconCircleCheck,
    IconAlertTriangle,
    IconAlertCircle,
    IconProps,
} from '@tabler/icons-react';
import { FC } from 'react';

const NEUTRAL = 'neutral';

const alertVariants = cva(styles.base, {
    variants: {
        status: {
            neutral: styles.neutral,
            info: styles.info,
            positive: styles.positive,
            caution: styles.caution,
            negative: styles.negative,
        },
    },
    defaultVariants: {
        status: NEUTRAL,
    },
});

const statusToIconMap: Record<NonNullable<VariantProps<typeof alertVariants>['status']>, FC<IconProps>> = {
    neutral: IconInfoCircle,
    info: IconInfoCircle,
    positive: IconCircleCheck,
    caution: IconAlertTriangle,
    negative: IconAlertCircle,
};

export type ShowAlertParams = NotificationData & VariantProps<typeof alertVariants>;

export const Alert = ({
    status,
    title,
    children,
    ...props
}: NotificationProps & VariantProps<typeof alertVariants>) => {
    const Icon = statusToIconMap[status ?? 'neutral'];
    return (
        <Notification
            className={alertVariants({ status })}
            title={
                <Group justify="start" gap="4px" align={'center'}>
                    <Icon className={'datavant-Alert-icon'} />
                    <Box maw={'90%'}>{title}</Box>
                </Group>
            }
            {...props}
        >
            {children}
        </Notification>
    );
};

export const showAlert = ({
    status,
    title,
    message,
    withCloseButton = true,
    ...props
}: ShowAlertParams): string => {
    const Icon = statusToIconMap[status ?? 'neutral'];
    return notifications.show({
        className: alertVariants({ status }),
        title: (
            <Group justify="start" gap="4px" align={'center'}>
                <Icon className={'datavant-Alert-icon'} />
                <Box maw={'90%'}>{title}</Box>
            </Group>
        ),
        message,
        withCloseButton,
        ...props,
    });
};
